/**
 * Spoken-Hungarian forms for the voice assistant. The model reads raw
 * "17:00–18:00" / "57 400 Ft" awkwardly — so every tool result carries a
 * `say` field with natural Hungarian ("délután öt és hat óra között",
 * "ötvenhétezer-négyszáz forint") and the instructions tell the model to
 * speak THOSE, never the digit forms.
 */

const ONES = ['', 'egy', 'kettő', 'három', 'négy', 'öt', 'hat', 'hét', 'nyolc', 'kilenc']
const ONES_ATTR = ['', 'egy', 'két', 'három', 'négy', 'öt', 'hat', 'hét', 'nyolc', 'kilenc']
const TEENS = ['tíz', 'tizenegy', 'tizenkettő', 'tizenhárom', 'tizennégy', 'tizenöt', 'tizenhat', 'tizenhét', 'tizennyolc', 'tizenkilenc']
const TEENS_ATTR = ['tíz', 'tizenegy', 'tizenkét', 'tizenhárom', 'tizennégy', 'tizenöt', 'tizenhat', 'tizenhét', 'tizennyolc', 'tizenkilenc']
const TENS = ['', '', 'husz', 'harminc', 'negyven', 'ötven', 'hatvan', 'hetven', 'nyolcvan', 'kilencven']

/** 0–999 as one Hungarian word. attributive: 2→"két" (kétezer, kétszáz). */
function underThousand(n: number, attributive: boolean): string {
	if (n === 0) return ''
	const ones = attributive ? ONES_ATTR : ONES
	const teens = attributive ? TEENS_ATTR : TEENS
	let out = ''
	const h = Math.floor(n / 100)
	const rest = n % 100
	if (h > 0) out += (h === 1 ? '' : ONES_ATTR[h]) + 'száz'
	if (rest === 0) return out
	if (rest < 10) return out + ones[rest]
	if (rest < 20) return out + teens[rest - 10]
	const t = Math.floor(rest / 10)
	const o = rest % 10
	if (t === 2) return out + (o === 0 ? 'húsz' : 'huszon' + ones[o])
	return out + TENS[t] + (o === 0 ? '' : ones[o])
}

/** Integer → Hungarian number words (0 … 999 999 999). */
export function numberToHu(n: number): string {
	n = Math.round(Math.abs(n))
	if (n === 0) return 'nulla'
	let out = ''
	const millions = Math.floor(n / 1000000)
	const thousands = Math.floor((n % 1000000) / 1000)
	const rest = n % 1000
	if (millions > 0) {
		out += (millions === 1 ? 'egy' : underThousand(millions, true)) + 'millió'
		if (thousands > 0 || rest > 0) out += ' '
	}
	if (thousands > 0) {
		out += (thousands === 1 && millions === 0 ? '' : underThousand(thousands, true)) + 'ezer'
		// Hungarian orthography joins the remainder with a hyphen above 2000;
		// for speech the hyphen just reads naturally.
		if (rest > 0) out += '-'
	}
	if (rest > 0) out += underThousand(rest, false)
	return out
}

/** 57400 → "ötvenhétezer-négyszáz forint". */
export function priceToHu(amount: number): string {
	return `${numberToHu(amount)} forint`
}

/** Spoken hour with napszak prefix, for slot starts (8–23). */
function hourSay(h: number): string {
	if (h === 12) return 'déli tizenkettő'
	if (h >= 5 && h <= 8) return `reggel ${ONES[h] || TEENS[h - 10]}`
	if (h >= 9 && h <= 11) return `délelőtt ${h === 9 ? 'kilenc' : TEENS[h - 10]}`
	if (h >= 13 && h <= 17) return `délután ${ONES[h - 12]}`
	if (h >= 18 && h <= 23) return `este ${h - 12 < 10 ? ONES[h - 12] : TEENS[h - 22]}`
	return numberToHu(h)
}

/** Bare 12-hour word for the slot END ("... és hat óra között").
    Attributive forms before "óra": két óra, tizenkét óra — never "kettő óra". */
function hourEndSay(h: number): string {
	if (h === 24 || h === 0) return 'éjfél'
	if (h === 12) return 'dél'
	const h12 = h > 12 ? h - 12 : h
	return `${h12 < 10 ? ONES_ATTR[h12] : TEENS_ATTR[h12 - 10]} óra`
}

/** "17:00" → "délután öt és hat óra között". */
export function slotTimeToHu(time: string): string {
	const h = parseInt(time.slice(0, 2), 10)
	if (Number.isNaN(h)) return time
	const end = hourEndSay(h + 1)
	// "és dél között" / "és éjfél között" read without the extra "óra".
	return `${hourSay(h)} és ${end} között`
}

const MONTHS_HU = ['január', 'február', 'március', 'április', 'május', 'június', 'július', 'augusztus', 'szeptember', 'október', 'november', 'december']
const DAYS_HU = ['vasárnap', 'hétfő', 'kedd', 'szerda', 'csütörtök', 'péntek', 'szombat']
const DAY_ORDINALS = ['', 'elseje', 'másodika', 'harmadika', 'negyedike', 'ötödike', 'hatodika', 'hetedike', 'nyolcadika', 'kilencedike', 'tizedike', 'tizenegyedike', 'tizenkettedike', 'tizenharmadika', 'tizennegyedike', 'tizenötödike', 'tizenhatodika', 'tizenhetedike', 'tizennyolcadika', 'tizenkilencedike', 'huszadika', 'huszonegyedike', 'huszonkettedike', 'huszonharmadika', 'huszonnegyedike', 'huszonötödike', 'huszonhatodika', 'huszonhetedike', 'huszonnyolcadika', 'huszonkilencedike', 'harmincadika', 'harmincegyedike']

/** "2026-08-07" → "ma" / "holnap" / "csütörtök, augusztus hetedike". */
export function dateToHu(date: string): string {
	const d = new Date(`${date}T00:00:00`)
	if (Number.isNaN(d.getTime())) return date
	const today = new Date()
	today.setHours(0, 0, 0, 0)
	const diff = Math.round((d.getTime() - today.getTime()) / 86400000)
	if (diff === 0) return 'ma'
	if (diff === 1) return 'holnap'
	return `${DAYS_HU[d.getDay()]}, ${MONTHS_HU[d.getMonth()]} ${DAY_ORDINALS[d.getDate()] || d.getDate() + '.'}`
}

/** Full slot: "csütörtök, augusztus hetedike, délután öt és hat óra között". */
export function slotToHu(date: string, time: string): string {
	return `${dateToHu(date)}, ${slotTimeToHu(time)}`
}
